import { useState, useEffect, FormEvent, useContext } from "react";
import { useParams } from "react-router";

import "./model-configuration-edit.scss";
import { useNavigate } from "react-router-dom";

import { Button, Form } from "react-bootstrap";
import { BsArrowLeft, BsSave } from "react-icons/bs";
import { GoArrowRight } from "react-icons/go";
import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import { IColumn, ITableRowData } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import Wizzard from "../../../shared/components/wizzard/wizzard";
import { roleWrite } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../shared/definitions/datatypes";
import useHttp from "../../../shared/hooks/use-http";
import { getAllData, postSingleNonTable } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { activeOptions, crossValidationType, modelConfigOptimizationOptions, timeframeOptions, modelConfigThreshholdOptions, timeseriesByUnitReqs, kfoldTimeseriesByNumberReqs, ambiguousLabelOptions } from "../../../shared/utils/static-values";
import { useBack } from "../../../shared/utils/use-back";

import { usePrompt } from "../../../shared/utils/use-prompt";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";

export interface IStep {
  index: number;
  stepName: string;
  fields: IColumn[];
}

export interface IPairedValues {
  name: string,
  value: string,
  id: string
}

const initialState: IStep[] = [
  {
    index: 1,
    stepName: "Setup Configuration",
    fields: [
      {
        key: "modelversion_name",
        displayName: "Model Name",
        required: true,
        dataType: {
          type: "text",
        },
        controlled: true
      },
      {
        key: "nthresholds",
        displayName: "Threshold granularity",
        required: true,
        dataType: {
          type: "enumeration",
          values: modelConfigThreshholdOptions,
        }
      },
      {
        key: "alert_only_dataset",
        displayName: "Alert only dataset",
        dataType: {
          type: "enumeration",
          values: activeOptions,
        },
        noEmptyDefault: true,
        required: false
      },
      {
        key: "alert_only_training",
        displayName: "Alert only training",
        dataType: {
          type: "enumeration",
          values: activeOptions,
        },
        noEmptyDefault: true,
        required: false
      },
      {
        key: "alert_only_reporting",
        displayName: "Alert only reporting",
        dataType: {
          type: "enumeration",
          values: activeOptions,
        },
        noEmptyDefault: true,
        required: false
      }
    ],
  },
  {
    index: 2,
    stepName: "Optimization",
    fields: [
      {
        key: "loss",
        displayName: "Optimization function",
        required: true,
        dataType: {
          type: "enumeration",
          values: modelConfigOptimizationOptions,
        },
      },
      {
        key: "fp_cost",
        displayName: "False Positive costs",
        required: true,
        dataType: {
          type: "text",
        },
        placeholder: "e.g. 150",
        subType: "number",
      },
      {
        key: "ambiguous_label",
        displayName: "Assign ambiguous label",
        required: true,
        dataType: {
          type: "enumeration",
          values: ambiguousLabelOptions
        },
      },
    ],
  },
  {
    index: 3,
    stepName: "Consolidation",
    fields: [
      {
        key: "column",
        displayName: "Select entity colum",
        required: true,
        dataType: {
          type: "reference",
          values: [],
        },
        placeholder: "select an existing entity label"
      },
      {
        key: "amount",
        displayName: "Select amount",
        required: true,
        dataType: {
          type: "reference",
          values: [],
        },
        placeholder: "select an existing amount label"
      },
      {
        key: "timeframe",
        displayName: "Timeframe",
        required: true,
        dataType: {
          type: "enumeration",
          values: timeframeOptions
        },
      },
      {
        key: "datetime",
        displayName: "Select datetime",
        required: true,
        dataType: {
          type: "reference",
          values: [],
        },
        placeholder: "select an existing datetime label"
      }
    ],
  },
  {
    index: 4,
    stepName: "Cross validation",
    fields: [
      {
        key: "cv_type",
        displayName: "Cross validation type",
        required: true,
        dataType: {
          type: "enumeration",
          values: crossValidationType,
        },
        noEmptyDefault: true
      },
      {
        key: "cv_n_splits",
        displayName: "Number of folds",
        required: true,
        dataType: {
          type: "text"
        },
        placeholder: "e.g. 5",
        onlyWholeNumbers: true
      },
    ],
  },
];

const ModelConfigurationEdit = () => {
  const [fields, setFields] = useState<IStep[]>([]);
  const [data, setData] = useState<ITableRowData>({ id: "", modelversion_name: "" });
  const [validated, setValidated] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const [allowedTabs] = useState([1, 2, 3, 4]);
  const [isDirty, setIsDirty] = useState(false);
  const [, setUnblockInput] = useState(false);

  const { handleBack } = useBack();
  const { flag, setFlag, next } = usePrompt(isDirty);
  const params = useParams();
  const authCtx = useContext(AuthContext);
  const rolesArray = authCtx.selectedTenant.roles;
  const navigate = useNavigate();

  const { sendRequest: postModelConfig, status: postModelConfigStatus, error: errorModelConfig } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getModelConfig, status: modelConfigStatus, data: modelConfigLoadedData,  error: errorGetConfig } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getDataset, status: datasetStatus, data: datasetLoadedData,  error: errorGetDataset } = useHttp(getAllData, false, authCtx.token, "dataset", "analytics");

  useEffect(() => {
    setFields(initialState);
  }, []);

  useEffect(() => {
    if (rolesArray.indexOf(roleWrite) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    getModelConfig(`modelconfig/${params.id}`);
  }, []);

  useEffect(() => {
    if (modelConfigStatus === "completed" && !errorGetConfig) {
      getDataset(`dataset/${modelConfigLoadedData.rows.dataset_id}`);
    }
  }, [modelConfigStatus]);

  useEffect(() => {
    if (postModelConfigStatus === "completed" && !errorModelConfig) {
      datasetLoadedData && navigate(`/analytics/datasets-details/${datasetLoadedData.id}`);
      next();
    }
  }, [postModelConfigStatus]);

  useEffect(() => {
    if (datasetStatus === "completed" && !errorGetDataset) {
      const allLabels = [...datasetLoadedData.labels];
      const resultDatatime:IEnumerationOption[] = [];
      const resultAmount:IEnumerationOption[] = [];
      const resultEntity:IEnumerationOption[] = [];

      let amountName = "";
      let datetimeName = "";
      let columnName = "";

      allLabels.forEach((label) => {
        if (label.id === modelConfigLoadedData.rows.column) {
          columnName = label.field_name;
        }
        if (label.id === modelConfigLoadedData.rows.datetime) {
          datetimeName = label.field_name;
        }
        if (label.id === modelConfigLoadedData.rows.amount) {
          amountName = label.field_name;
        }
      });
      const loadedData = {
        ...modelConfigLoadedData.rows,
        column: columnName,
        column_id: modelConfigLoadedData.rows.column,
        datetime: datetimeName,
        datetime_id: modelConfigLoadedData.rows.datetime,
        amount: amountName,
        amount_id: modelConfigLoadedData.rows.amount,
      };
      setData(loadedData);

      allLabels.forEach((label) => {
        if (label.label === "datetime") {
          resultDatatime.push({id: label.id, name: label.field_name});
        }
        if (label.label === "amount") {
          resultAmount.push({id: label.id, name: label.field_name});
        }
        if (label.label === "entity") {
          resultEntity.push({id: label.id, name: label.field_name});
        }
      });

      const result = fields.map((field) => {
        if (field.stepName === "Consolidation") {
          return {...field, fields: field.fields.map((consolidationField) => {
            if (consolidationField.key === "datetime") {
              return {...consolidationField, dataType: {...consolidationField.dataType, values: resultDatatime}};
            }
            if (consolidationField.key === "amount") {
              return {...consolidationField, dataType: {...consolidationField.dataType, values: resultAmount}};
            }
            if (consolidationField.key === "column") {
              return {...consolidationField, dataType: {...consolidationField.dataType, values: resultEntity}};
            }

            return consolidationField;
          })};
        }
        return field;
      });
      setFields(result);
    }
  }, [datasetStatus]);

  const getSelectedTab = (tab: number) => {
    setSelectedTab(tab);
  };

  const handleOnChange = (targetVal: string | boolean | null, inputKey: string, id: string) => {
    if (modelConfigStatus === "completed" && !errorGetConfig) {
      setIsDirty(true);
    }
    if (id && id !== inputKey) {
      setData((prevState) => {return {...prevState, [inputKey]: targetVal, [`${inputKey}_id`] : id};});
    } else {
      setData((prevState) => {return {...prevState, [inputKey]: targetVal};});
    }
  };

  const filterData = () => {
    let dataCopy = {...data};
    if ((data.cv_type === "kfold" || data.cv_type === "timeseries_by_n_transactions") && (data.cv_time_unit || data.cv_test_timespan || data.cv_train_timespan || data.cv_with_future )) {
      delete dataCopy["cv_train_timespan"];
      delete dataCopy["cv_test_timespan"];
      delete dataCopy["cv_with_future"];
      delete dataCopy["cv_time_unit"];
    } 

    dataCopy = {...dataCopy, amount: dataCopy.amount_id, datetime: dataCopy.datetime_id, column: dataCopy.column_id};
    delete dataCopy["amount_id"];
    delete dataCopy["datetime_id"];
    delete dataCopy["column_id"];
    delete dataCopy["amount_name"];
    delete dataCopy["datetime_name"];
    delete dataCopy["column_name"];
    
    return dataCopy;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();

    if (form.checkValidity() === true) {
      const filteredData = filterData();
      postModelConfig("modelconfig", {...filteredData, id: data.id, dataset_id: modelConfigLoadedData.rows.dataset_id});
      next();
    }
  };

  const handleNextTabSelection = () => {
    if (selectedTab !== 4) {
      const findIndex = allowedTabs.indexOf(selectedTab);
      setSelectedTab(allowedTabs[findIndex + 1]);
      // next
    } else {
      // invalid
      setValidated(true);
    }
  };

  const renderSaveBtnBeforeLastTab = () => {
    let showBtn = true;
    if (data.cv_type === "kfold" || data.cv_type === "timeseries_by_n_transactions") {
      kfoldTimeseriesByNumberReqs.forEach((fieldKey: string) => {
        if (!data[`${fieldKey}`]) {
          showBtn = false;
        }
      });
    } else {
      timeseriesByUnitReqs.forEach((fieldKey: string) => {
        if ((!data[`${fieldKey}`] && !data[`${fieldKey}`] !== false) || data.cv_with_future === "") {
          showBtn = false;
        }
      });
    }
    return (
      <Tooltip content={"Save"} direction="top" delay={350}>
        <Button disabled={!showBtn} className="modelconfig-save" type="submit" variant="primary">
          <BsSave />
        </Button>
      </Tooltip>
    );
  };

  const renderFields = () => {
    if (fields.length !== 0) {
      const num = selectedTab - 1;
      const result = [...fields[num].fields];

      if (selectedTab === 4 && data.cv_type === "timeseries_by_time_unit") {
        result.splice(result.length - 1, 0 , 
          {
            key: "cv_time_unit",
            displayName: "Time unit",
            required: true,
            dataType: {
              type: "enumeration",
              values: timeframeOptions
            },
          },
          {
            key: "cv_train_timespan",
            displayName: "Training period",
            required: true,
            dataType: {
              type: "text"
            },
            placeholder: "e.g. 1",
            subType: "number",
            onlyWholeNumbers: true
          },
          {
            key: "cv_test_timespan",
            displayName: "Testing period",
            required: true,
            dataType: {
              type: "text"
            },
            placeholder: "e.g. 1",
            subType: "number",
            onlyWholeNumbers: true
          },
          {
            key: "cv_with_future",
            displayName: "Include future data for training",
            required: true,
            dataType: {
              type: "enumeration",
              values: activeOptions,
            },
          },
        );
      }
      return (
        <RenderFields
          mode="aside"
          fields={result}
          data={data}
          handleOnChange={handleOnChange}
        />
      );
    }
  };

  const renderPathButtons = () => {
    return (
      <span className="optionPathsWrpr">
        <span onClick={() => navigate("/analytics/datasets")} className="optionPath">Datasets</span>
        <span className="optionPathDelimiter">-</span>
        <span onClick={() => datasetLoadedData && navigate(`/analytics/datasets-details/${datasetLoadedData.id}`)} className="optionPath">Dataset</span>
        <span className="optionPathDelimiter">-</span>
        <span className="selectedPathBtn">Model config</span>
      </span>
    );
  };
  
  return (
    <div className="modelconfig">
      {flag && <ConfirmationPopup actionButtonDisabled={validateLoadingStatusNew([postModelConfigStatus])} show={flag} handlePopupClosed={() => setFlag(false)} handleContinue={() => { next(); setFlag(false); }} />}

      {validateLoadingStatusNew([postModelConfigStatus, datasetStatus, modelConfigStatus]) && <Spinner />}

      <div onClick={() => handleBack()} className="listPage-nav-btn">
        <BsArrowLeft />
      </div>

      <div className="modelconfig-header">
        <h1 >
          {data.modelversion_name && data.modelversion_name}
          <Tooltip content={"Generating a model requires the following labels: amount, datetime, entity"} direction="top" delay={350} />
        </h1>
        {renderPathButtons()}
      </div>

      <div className="modelconfig-aside">
        <Wizzard isFullscreen={false} hideDisabledTabs={true} noNumeration={true} selectedTabFromParent={selectedTab} allowedTabs={allowedTabs} getSelectedTab={getSelectedTab} data={fields}>

          <Form id="controlledForm" noValidate validated={validated} onSubmit={handleSubmit}>
            {renderFields()}
            <div className="datasetNew-buttonsGroup">

              {selectedTab !== 4 && <Tooltip content={"Next"} direction="top" delay={350}>
                <Button onClick={() => handleNextTabSelection()} className="modelconfig-save" variant="primary">
                  {<GoArrowRight />}
                </Button>
              </Tooltip>}

              {renderSaveBtnBeforeLastTab()}
            </div>
          </Form>
        </Wizzard>
      </div>
    </div>
  );
};

export default ModelConfigurationEdit;
